import React, { createContext, useReducer } from 'react'

import { isPast } from 'date-fns'
import { merge } from 'lodash'

import { naturalSortOn } from 'utils/array'
import { freeze } from 'utils/dom'

const defaultAddr = freeze({
  street: '',
  city: '',
  state: '',
  postal: '',
  country: ''
})
const defaultState = freeze({
  id: '',
  name: '',
  contacts: {},
  phone: '',
  website: '',
  companyaddr: { ...defaultAddr },
  billingaddr: { ...defaultAddr },
  servers: [],
  members: [],
  pendingInvites: [],
  admincontact: undefined,
  billingcontact: undefined,
  operationscontact: undefined,
  securitycontact: undefined,
  stripe: undefined,
  onSave: undefined
})

function normalizeData(data) {
  const result = data.reduce((obj, data) => {
    const value = data.value?.value
    obj[data.type] = ['companyaddr', 'billingaddr'].includes(data.type)
      ? { ...defaultAddr, ...value }
      : value
    return obj
  }, {})
  return result
}

function normalizePendingInvites(pendingInvites) {
  return pendingInvites
    .toSorted(naturalSortOn('email'))
    .filter(({ used, expiresAt }) => !used || isPast(new Date(expiresAt))) // should be filtered out on BE but just in case
}

export function normalizeOrg(update) {
  return {
    ...defaultState,
    ...update,
    ...normalizeData(update.data || []),
    pendingInvites: normalizePendingInvites(update.pendingInvites)
  }
}

export const R_ORG = {
  SET: 0,
  RESET: 1,
  MERGE: 2,
  DELETE: 3,
  IGNORE: 4
}

function reducer(state, { type, value, ...args }) {
  switch (type) {
    case R_ORG.SET:
      const org = normalizeOrg(value)
      // use merge not spread here -- onSave: undefined will overwrite previous
      return state.id === value.id ? merge({}, state, org) : org
    case R_ORG.IGNORE:
      return state
    case R_ORG.MERGE:
      return { ...state, ...value }
    case R_ORG.RESET:
    case R_ORG.DELETE:
      return { ...defaultState, onSave: state.onSave }
    default:
      throw new Error(`OrgReducer - no such action.type: ${type}!`)
  }
}

export const OrgContext = createContext(null)
export function OrgProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, defaultState)
  return (
    <OrgContext.Provider value={[state, dispatch]}>
      {children}
    </OrgContext.Provider>
  )
}

export default OrgContext
